import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Toaster } from 'react-hot-toast';
import LeftPanel from "./components/LeftPanel";
import PanelsContainer from "./containers/PanelsContainer";
import MainPanelContainer from "./containers/MainPanelContainer";
import usePanels from "./hooks/usePanels";
import { useAuth } from './hooks/useAuth';
import { useTheme } from "./hooks/useTheme";
import { useModelSettings } from "./hooks/useModelSettings";
import { useApiService } from "./hooks/useApiService";
import { useChatSessions } from "./hooks/useChatSessions";
import { useSwarmSession } from "./hooks/useSwarmSession";
import { useWorkflow } from "./hooks/useWorkflow";
import { useSwarmHistories } from "./hooks/useSwarmHistories";
import { useDeleteSwarmSession } from "./hooks/useDeleteSwarmSession";
import { useRenameSwarmSession } from "./hooks/useRenameSwarmSession";
import { useResetChat } from "./hooks/useResetChat";
import { useConversationHistory } from "./hooks/useConversationHistory";
import { useDeleteChat } from "./hooks/useDeleteChat";
import { useRenameChat } from "./hooks/useRenameChat";
import { getAppStyles } from "./utils/appStyles";
import { historyEntryFromServer } from "./utils/chatEntries";
import { selectUnsyncedTerminalRunKeys } from "./utils/workflowRunSync";
import { resolveWorkflowSelection } from "./utils/workflowSelection";
import TutorialContent from "./components/content/TutorialContent";

function getOrCreateConversationId() {
  return crypto.randomUUID();
}

function App() {
    const { user, dismissTutorial, restoreTutorial } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const modelSettings = useModelSettings();
    const [selectedApis, setSelectedApis] = useState("orchestrator");
    const [activeMode, setActiveMode] = useState("ask");
    const { API_URL, availableAgents } = useApiService(selectedApis);
    const [conversationId, setConversationId] = useState(getOrCreateConversationId);
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [historyRefreshTrigger, setHistoryRefreshTrigger] = useState(0);
    const [showTutorial, setShowTutorial] = useState(false);

    // Input state — shared across all conversations (the user's current draft).
    const [question, setQuestion] = useState("");
    const [files, setFiles] = useState([]);
    const [answerMode, setAnswerMode] = useState({
        concise: false,
        expansive: false,
        evaluation: false,
        auto: true,
    });

    // Ensure answerMode defaults to Auto when all options are unchecked.
    useEffect(() => {
        if (!answerMode.auto && !answerMode.concise && !answerMode.expansive && !answerMode.evaluation) {
            setAnswerMode({ concise: false, expansive: false, evaluation: false, auto: true });
        }
    }, [answerMode]);

    useEffect(() => {
        if (user?.show_tutorial) {
            setShowTutorial(true);
        }
    }, [user?.show_tutorial]);

    const userId = user?.id;

    const refreshHistories = useCallback(() => setHistoryRefreshTrigger(prev => prev + 1), []);
    const { refreshModelSettings } = modelSettings;

    // Multi-session chat registry — each conversation runs independently.
    const chatSessions = useChatSessions(API_URL, modelSettings);

    // Pending entries shown in the sidebar immediately when a request launches,
    // before the first history re-fetch confirms the DB record.
    // The DB record (status='running') is the durable source of truth;
    // this state just provides instant visual feedback in the current session.
    const [pendingConversations, setPendingConversations] = useState([]);
    const [pendingSwarmSessions, setPendingSwarmSessions] = useState([]);

    // IDs that finished in this browser session — used to immediately clear the
    // sidebar spinner even if the DB re-fetch hasn't confirmed 'completed' yet
    // (guards against Supabase read-after-write lag).
    const [justCompletedIds, setJustCompletedIds] = useState(() => new Set());

    // True when the DB has any status='running' conversation (cross-session
    // scenario: user closed browser while processes were running, then reopened).
    const [hasRunningHistories, setHasRunningHistories] = useState(false);
    // Set of conversation IDs currently running server-side.
    const [runningConversationIds, setRunningConversationIds] = useState(() => new Set());
    const hasRunningHistoriesRef = useRef(false);
    useEffect(() => { hasRunningHistoriesRef.current = hasRunningHistories; }, [hasRunningHistories]);
    // Derive hasRunningHistories reactively so callers never need to read
    // runningConversationIds from a potentially stale closure.
    useEffect(() => {
        setHasRunningHistories(runningConversationIds.size > 0);
    }, [runningConversationIds]);

    // Stable refs for values used inside onHistoriesFetched without re-creating it.
    const conversationIdRef = useRef(conversationId);
    useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);
    // Tracks which conversation IDs were running on the last fetch so we can
    // detect the running→completed transition for auto-reload.
    const prevRunningIdsRef = useRef(new Set());
    // chatSessions is a new object on every useChatSessions render, so using it
    // directly in onHistoriesFetched's deps would recreate the callback every time
    // loadSessionHistory updates sessions — triggering ChatHistoryList's
    // useEffect([histories, onHistoriesFetched]) and causing an infinite fetch loop.
    // A ref breaks the cycle: the latest chatSessions is always accessible without
    // onHistoriesFetched needing it as a dep.
    const chatSessionsRef = useRef(chatSessions);
    useEffect(() => { chatSessionsRef.current = chatSessions; }, [chatSessions]);

    // Poll every 8 s while there are server-side running conversations so the
    // sidebar spinner clears automatically when the background process finishes.
    useEffect(() => {
        if (!hasRunningHistories) return;
        const id = setInterval(refreshHistories, 8000);
        return () => clearInterval(id);
    }, [hasRunningHistories, refreshHistories]);

    const { fetchConversationHistory } = useConversationHistory(API_URL);

    // When the sidebar fetches DB histories, drop in-memory pending entries whose
    // DB record has taken over, clear justCompletedIds for confirmed completions,
    // track whether any conversations are still running, and auto-reload the
    // main panel when the active conversation transitions running→completed.
    const onHistoriesFetched = useCallback((fetchedHistories) => {
        const fetchedIds = new Set(fetchedHistories.map(h => h.conversation_id));
        setPendingConversations(prev => {
            const next = prev.filter(p => !fetchedIds.has(p.conversation_id));
            return next.length === prev.length ? prev : next;
        });
        setJustCompletedIds(prev => {
            if (prev.size === 0) return prev;
            const next = new Set(prev);
            fetchedHistories.forEach(h => {
                if (h.status !== 'running') next.delete(h.conversation_id);
            });
            return next.size === prev.size ? prev : next;
        });

        const currentRunningIds = new Set(
            fetchedHistories.filter(h => h.status === 'running').map(h => h.conversation_id)
        );
        setHasRunningHistories(currentRunningIds.size > 0);
        setRunningConversationIds(currentRunningIds);

        // Detect conversations that just transitioned running → completed.
        const justFinished = fetchedHistories.filter(
            h => h.status !== 'running' && prevRunningIdsRef.current.has(h.conversation_id)
        );
        prevRunningIdsRef.current = currentRunningIds;

        // Shared entry mapper used by both reload paths below.
        // Reload content for every conversation that just completed so results
        // appear without a manual refresh, regardless of which one is active.
        if (justFinished.length > 0 && userId) {
            justFinished.forEach(h => {
                const session = chatSessionsRef.current.getSession(h.conversation_id);
                if (session.loading) return; // don't clobber an active stream
                fetchConversationHistory(userId, h.conversation_id).then(historyFromServer => {
                    if (!historyFromServer?.length) return;
                    const mapped = historyFromServer
                        .map(historyEntryFromServer)
                        .sort((a, b) => a.index - b.index);
                    // forceServer=true: server has the definitive completed data;
                    // in-memory is stale (loaded at login before the process
                    // finished). Merging would overwrite serverEntry.assistant
                    // (expansive) with memEntry.assistant (old concise), making
                    // hasPreliminary=false and leaving the button green.
                    chatSessionsRef.current.loadSessionHistory(h.conversation_id, mapped, true);
                }).catch(e => console.error('Failed to reload completed conversation:', e));
            });
        }

        // Note: showing the concise answer while expansive is still running is
        // handled by the dedicated 3 s effect below (isActiveConvServerRunning +
        // chatHistory.length === 0).  The justFinished path above covers the
        // running→completed transition.  No additional reload needed here.
    }, [userId, fetchConversationHistory]); // chatSessions intentionally omitted — use chatSessionsRef

    // Ref lets the visibilitychange handler read the latest pendingConversations
    // without needing to re-register the listener on every state change.
    const pendingConvRef = useRef(pendingConversations);
    useEffect(() => { pendingConvRef.current = pendingConversations; }, [pendingConversations]);

    // Re-sync history when returning to the tab OR regaining connectivity,
    // if there are pending or running conversations that may have completed
    // while away.  Mobile suspends timers when backgrounded and drops the
    // connection on network handoff, so both the visibility and the online
    // signals are needed to pull in a server-completed answer promptly.
    useEffect(() => {
        const maybeRefresh = () => {
            if (pendingConvRef.current.length > 0 || hasRunningHistoriesRef.current) {
                refreshHistories();
            }
        };
        const onVisible = () => {
            if (document.visibilityState === 'visible') maybeRefresh();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('online', maybeRefresh);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('online', maybeRefresh);
        };
    }, [refreshHistories]);

    // Active session state derived from the registry.
    const activeSession = chatSessions.getSession(conversationId);
    const chatHistory = activeSession.chatHistory;
    const response = activeSession.response;
    const loading = activeSession.loading;
    const elapsed = activeSession.elapsed;
    const progressSteps = activeSession.progressSteps;
    const pendingQuestion = activeSession.pendingQuestion;
    const pendingFiles = activeSession.pendingFiles;
    const streamingPhase = activeSession.streamingPhase;
    const generatingExpansiveIndex = activeSession.generatingExpansiveIndex;
    const generatingEvaluationIndex = activeSession.generatingEvaluationIndex;

    // Callback fired when any conversation finishes — refreshes the history list
    // and marks that conversation active if it's the one currently in view.
    const onAfterAnswer = useCallback((completedConversationId) => {
        refreshHistories();
        setPendingConversations(prev => prev.filter(p => p.conversation_id !== completedConversationId));
        // Immediately mark as done so the sidebar spinner clears even if the DB
        // re-fetch hasn't confirmed 'completed' yet (Supabase read-after-write lag).
        setJustCompletedIds(prev => new Set([...prev, completedConversationId]));
        // Use the ref so we read the *current* conversationId, not the one
        // captured when onAfterAnswer was created.  Without this, switching
        // conversations while a chain runs causes the stale closure value to
        // match completedConversationId and overwrite the new activeConversationId.
        if (completedConversationId === conversationIdRef.current) {
            setActiveConversationId(completedConversationId);
        }
    }, [refreshHistories]);

    // Stable wrappers that always operate on the currently-visible conversation.
    const askQuestion = useCallback((e) => {
        if (e) e.preventDefault();
        const launched = chatSessions.askQuestion(conversationId, question, files, onAfterAnswer);
        if (launched) {
            const name = question.trim().split(/\s+/).slice(0, 6).join(' ') || 'New conversation';
            setPendingConversations(prev => [
                ...prev.filter(p => p.conversation_id !== conversationId),
                { conversation_id: conversationId, name, last_modified: new Date().toISOString(), isPending: true },
            ]);
            setQuestion("");
            setFiles([]);
        }
        return launched;
    }, [chatSessions, conversationId, question, files, onAfterAnswer, setQuestion, setFiles]);

    const cancelRequest = useCallback((acceptAnswer = false) => {
        chatSessions.cancelRequest(conversationId, acceptAnswer, onAfterAnswer);
    }, [chatSessions, conversationId, onAfterAnswer]);

    // True when the server-side ask is still running for the active conversation
    // but this browser session has no active stream (cross-session scenario).
    const isActiveConvServerRunning = runningConversationIds.has(conversationId) && !loading;

    // Poll DB content every 3 s while the active conversation is running
    // cross-session.  Fires immediately and continues even after concise
    // content loads so we catch the concise → full-content transition
    // (Phase 1 → Phase 2 complete) without waiting up to 8 s for the next
    // sidebar poll.  The effect tears down when isActiveConvServerRunning
    // becomes false (sidebar poll detected status="completed").
    //
    // chatHistory is intentionally NOT in the deps: we never want the
    // interval to restart just because content arrived — a restart would
    // create a gap and lose the smooth progressive reveal.
    useEffect(() => {
        if (!isActiveConvServerRunning || !userId) return;

        const targetId = conversationId;

        const tryFetch = async () => {
            const session = chatSessionsRef.current.getSession(targetId);
            // Skip while a local stream is active — it owns the content.
            if (session.loading) return;

            const history = await fetchConversationHistory(userId, targetId).catch(() => []);
            if (!history?.length) return;

            const mapped = history
                .map(historyEntryFromServer)
                .sort((a, b) => a.index - b.index);

            const current = chatSessionsRef.current.getSession(targetId);
            const noContent = current.chatHistory.length === 0;
            // New entry appeared (Phase 1 completed for a new question in
            // an existing conversation that previously had no entry at
            // this index).
            const hasNewEntries =
                mapped.length > current.chatHistory.length;
            // Phase 2 completed specifically for the latest entry. Uses
            // per-entry comparison to avoid false positives from older
            // entries that already have preliminary_answer.
            const lastMapped = mapped[mapped.length - 1];
            const lastCurrent =
                current.chatHistory[current.chatHistory.length - 1];
            const expansiveLandedOnLatest = !!(
                lastMapped && lastCurrent &&
                lastMapped.index === lastCurrent.index && (
                    (
                        !lastCurrent.preliminary_answer &&
                        lastMapped.preliminary_answer
                    ) || (
                        lastMapped.assistant &&
                        lastCurrent.assistant &&
                        lastMapped.assistant !== lastCurrent.assistant &&
                        !!lastMapped.preliminary_answer
                    )
                )
            );

            if (noContent || hasNewEntries || expansiveLandedOnLatest) {
                chatSessionsRef.current.loadSessionHistory(
                    targetId, mapped, true
                );
                // Kick the sidebar poll when Phase 2 completed on the
                // latest entry so runningConversationIds clears and
                // spinners stop.
                if (expansiveLandedOnLatest) {
                    refreshHistories();
                }
            }
        };

        tryFetch(); // run immediately on activation
        const id = setInterval(tryFetch, 3000);
        return () => clearInterval(id);
    }, [isActiveConvServerRunning, conversationId, userId, fetchConversationHistory, refreshHistories]);

    // Force-complete a cross-session running conversation so the concise answer
    // is treated as final and the sidebar spinner clears immediately.
    const stopAsk = useCallback(async () => {
        try {
            await fetch(`${API_URL}/stop_ask`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ conversation_id: conversationId }),
                credentials: 'include',
            });
        } catch (e) {
            console.error('Failed to stop ask:', e);
        }
        setRunningConversationIds(prev => {
            const next = new Set(prev);
            next.delete(conversationId);
            return next;
            // hasRunningHistories is derived reactively via the useEffect
            // above — no manual setHasRunningHistories call needed here.
        });
        refreshHistories();
    }, [API_URL, conversationId, refreshHistories]);

    const generateExpansiveAnswer = useCallback(
        (messageIndex, onExpansiveReady) => {
            return chatSessions.generateExpansiveAnswer(
                conversationId, messageIndex, onAfterAnswer, onExpansiveReady,
            );
        },
        [chatSessions, conversationId, onAfterAnswer],
    );

    const generateEvaluationReport = useCallback((messageIndex, switchToEvalCallback) => {
        return chatSessions.generateEvaluationReport(conversationId, messageIndex, switchToEvalCallback, onAfterAnswer);
    }, [chatSessions, conversationId, onAfterAnswer]);

    // Set of conversation IDs that are currently loading — used by the sidebar.
    const loadingConversationIds = chatSessions.getLoadingIds();

    const swarm = useSwarmSession(API_URL, conversationId, modelSettings);

    // ── Workflow mode ────────────────────────────────────────────────────────
    const {
        workflows: workflowList,
        activeRuns: workflowActiveRuns,
        listWorkflows,
        runWorkflow,
        cancelRun: cancelWorkflowRun,
        listUserRuns: listUserWorkflowRuns,
        renameRun: renameWorkflowRun,
        deleteRun: deleteWorkflowRun,
    } = useWorkflow();

    // Persisted workflow run history shown in the left panel
    const [userWorkflowRuns, setUserWorkflowRuns] = useState([]);

    const loadUserWorkflowRuns = useCallback(async () => {
        const runs = await listUserWorkflowRuns(50);
        // null = fetch failure: keep the cached list (don't wipe the
        // sidebar) and report failure so callers can retry.
        if (runs === null) return false;
        setUserWorkflowRuns(runs);
        return true;
    }, [listUserWorkflowRuns]);

    useEffect(() => { loadUserWorkflowRuns(); }, [loadUserWorkflowRuns]);

    const handleRenameWorkflowRun = useCallback(async (workflowId, runId, name) => {
        const ok = await renameWorkflowRun(workflowId, runId, name);
        if (ok) setUserWorkflowRuns((prev) =>
            prev.map((r) => r.id === runId ? { ...r, name } : r)
        );
        return ok;
    }, [renameWorkflowRun]);

    const handleDeleteWorkflowRun = useCallback(async (workflowId, runId) => {
        const ok = await deleteWorkflowRun(workflowId, runId);
        if (ok) setUserWorkflowRuns((prev) => prev.filter((r) => r.id !== runId));
        return ok;
    }, [deleteWorkflowRun]);

    const [activeWorkflowId, setActiveWorkflowId] = useState(null);
    // Array<{ id, question, runId, workflowId, workflowName, output_json, error_message, status }>
    const [workflowRunHistory, setWorkflowRunHistory] = useState([]);

    // Load a past workflow run into the main chat view so the user can review
    // its output without re-running.  Fetches the full run (including
    // output_json which is omitted from the sidebar list) then synthesises a
    // workflowRunHistory entry identical in shape to a live run entry.
    const handleSelectWorkflowRun = useCallback(async (run) => {
        try {
            const res = await fetch(
                `${API_URL}/workflows/${run.workflow_id}/runs/${run.id}`,
                { credentials: 'include' },
            );
            if (!res.ok) return;
            const fullRun = await res.json();

            // Derive the user-visible question from input_json
            const inp = fullRun.input_json;
            const question = (inp && typeof inp === 'object')
                ? String(Object.values(inp).find(v => v) || '')
                : '';

            // Find workflow name from list or fall back to run's joined field
            const wf = workflowList.find((w) => w.id === run.workflow_id);
            const workflowName = wf?.name || run.workflow_name || run.workflow_id;

            const historyEntry = {
                id: crypto.randomUUID(),
                question,
                runId: fullRun.id,
                workflowId: run.workflow_id,
                workflowName,
                workflowIcon: wf?.icon || run.workflow_icon || null,
                output_json: fullRun.output_json || null,
                error_message: fullRun.error_message || null,
                status: fullRun.status,
            };

            // Replace the view with just this run — clicking a history
            // item navigates to that run, not appends to the current view.
            setWorkflowRunHistory([historyEntry]);

            // Select the workflow and switch to workflow mode
            if (run.workflow_id) setActiveWorkflowId(run.workflow_id);
            setActiveMode('workflow');
        } catch (_) { /* non-fatal */ }
    }, [API_URL, workflowList, setActiveWorkflowId]);

    useEffect(() => { listWorkflows(); }, [listWorkflows]);

    // Terminal runs whose left-panel refetch already fired, keyed by
    // `${runId}:${status}` — a finished run lingers in activeRuns while
    // other runs keep streaming, and the event-driven SSE stream would
    // otherwise turn that into one refetch per frame.
    const syncedTerminalRunsRef = useRef(new Set());

    // Copy completed/failed run state into workflowRunHistory entries so
    // past runs remain visible after activeRuns is overwritten by a new run.
    // Scans ALL active runs (not just the selected workflow) so concurrent
    // runs that finish while the user is viewing a different workflow are
    // still updated correctly.
    useEffect(() => {
        const terminal = ['completed', 'failed', 'cancelled'];
        const terminalRuns = Object.values(workflowActiveRuns).filter(
            (r) => r && r.runId && terminal.includes(r.status)
        );
        if (terminalRuns.length === 0) return;
        const byRunId = Object.fromEntries(terminalRuns.map((r) => [r.runId, r]));
        setWorkflowRunHistory(prev => prev.map(entry => {
            const runState = byRunId[entry.runId];
            if (runState && entry.status !== runState.status) {
                return {
                    ...entry,
                    output_json: runState.output_json,
                    error_message: runState.error_message,
                    status: runState.status,
                };
            }
            return entry;
        }));
        // Refresh the left-panel run list once per run termination — skip
        // runs already synced so a lingering terminal run doesn't refetch
        // on every SSE frame of a concurrent streaming run. A genuinely
        // new terminal transition (new runId:status key) still refetches.
        const newKeys = selectUnsyncedTerminalRunKeys(
            terminalRuns, syncedTerminalRunsRef.current,
        );
        if (newKeys.length === 0) return;
        // Record keys only after the refetch succeeds — a transient
        // failure leaves them unsynced so the next SSE frame retries
        // instead of latching a stale sidebar.
        loadUserWorkflowRuns().then((ok) => {
            if (ok) {
                newKeys.forEach(
                    (key) => syncedTerminalRunsRef.current.add(key),
                );
            }
        });
    }, [workflowActiveRuns, loadUserWorkflowRuns]);

    // Set of run IDs that are currently pending or running — passed to the
    // sidebar so WorkflowRunsList can show a spinner on in-flight runs.
    const runningWorkflowRunIds = useMemo(() => {
        const active = new Set(['pending', 'running']);
        return new Set(
            Object.values(workflowActiveRuns)
                .filter(r => r?.runId && active.has(r?.status))
                .map(r => r.runId)
        );
    }, [workflowActiveRuns]);

    const handleWorkflowSelect = useCallback((workflowId) => {
        // Clear any files attached in Ask/Swarm mode — workflows don't
        // accept file uploads, so lingering files would be silently dropped.
        setFiles([]);
        // The selection decision (toggle + always-clear) is a pure helper so
        // it stays unit-testable; deselecting keeps us in workflow mode so the
        // user sees "Select a workflow to start a run." rather than being
        // silently switched to Ask mode. clearResults resets the results panel
        // so a prior workflow's output can't linger beside a new selection.
        const next = resolveWorkflowSelection(workflowId, activeWorkflowId);
        if (next.clearResults) {
            setWorkflowRunHistory([]);
        }
        setActiveWorkflowId(next.activeWorkflowId);
        setActiveMode(next.mode);
    }, [activeWorkflowId, setFiles]);

    const handleRunWorkflow = useCallback(async (question) => {
        if (!activeWorkflowId) return;
        const wf = workflowList.find(w => w.id === activeWorkflowId);
        if (!wf) return;

        // Map question text → first input node's variable_name
        const inputNodes = (wf.dag_json?.nodes || [])
            .filter(n => n.config?.node_type === 'input');
        const inputValues = {};
        if (inputNodes.length > 0 && question.trim()) {
            inputValues[inputNodes[0].config.variable_name] = question;
        }

        // Always reset to a single fresh entry — each Run click starts a new
        // clean view regardless of what was shown before (past run or prior run).
        const historyId = crypto.randomUUID();
        setWorkflowRunHistory([{
            id: historyId,
            question: question.trim(),
            runId: null,
            workflowId: activeWorkflowId,
            workflowName: wf.name,
            workflowIcon: wf.icon || null,
            output_json: null,
            error_message: null,
            status: 'pending',
        }]);

        const runId = await runWorkflow(activeWorkflowId, inputValues);
        if (runId) {
            setWorkflowRunHistory(prev => prev.map(e =>
                e.id === historyId ? { ...e, runId, status: 'running' } : e
            ));
            // Refresh sidebar immediately so the new run appears with its
            // spinner rather than waiting until completion.
            loadUserWorkflowRuns();
        } else {
            setWorkflowRunHistory(prev => prev.map(e =>
                e.id === historyId
                    ? { ...e, status: 'failed', error_message: 'Failed to start run.' }
                    : e
            ));
        }
    }, [activeWorkflowId, workflowList, runWorkflow, loadUserWorkflowRuns]);
    // ── End workflow mode ────────────────────────────────────────────────────

    const [activeSwarmSessionId, setActiveSwarmSessionId] = useState(null);
    const [swarmHistoryRefreshTrigger, setSwarmHistoryRefreshTrigger] = useState(0);
    const refreshSwarmHistories = useCallback(
      () => setSwarmHistoryRefreshTrigger(prev => prev + 1),
      []
    );
    const { sessions: swarmSessions, loading: swarmSessionsLoading } =
      useSwarmHistories(API_URL, userId, swarmHistoryRefreshTrigger);
    const { deleteSwarmSession, isDeleting: isDeletingSwarm } = useDeleteSwarmSession(API_URL);
    const { renameSwarmSession, isRenaming: isRenamingSwarm } = useRenameSwarmSession(API_URL);

    // Called by MainPanel right before startSwarm so the entry appears immediately.
    const onSwarmStart = useCallback((q) => {
        const tempId = `pending-${conversationId}`;
        setPendingSwarmSessions(prev => [
            ...prev.filter(s => s.conversation_id !== conversationId),
            { session_id: tempId, conversation_id: conversationId, question: q, isPending: true, created_at: new Date().toISOString() },
        ]);
    }, [conversationId]);

    // Once the server assigns a real session_id, update the pending entry.
    useEffect(() => {
        if (swarm.sessionId) {
            setPendingSwarmSessions(prev => prev.map(s =>
                s.conversation_id === conversationId && s.session_id.startsWith('pending-')
                    ? { ...s, session_id: swarm.sessionId }
                    : s
            ));
        }
    }, [swarm.sessionId, conversationId]);

    // A swarm session is "loading" for as long as it has a pending entry.
    // Pending entries are added on start and removed only when isTerminal fires,
    // so this spinner persists regardless of which conversation is currently active.
    const loadingSwarmSessionIds = useMemo(
        () => new Set(pendingSwarmSessions.map(s => s.session_id)),
        [pendingSwarmSessions]
    );

    const resetMainPanel = useCallback(() => {
        const newId = crypto.randomUUID();
        setConversationId(newId);
        setActiveConversationId(null);
        setActiveSwarmSessionId(null);
        sessionStorage.setItem("conversationId",newId);
        // Don't clear the question input - preserve user's work in progress
        swarm.resetSwarm();
        // Clear workflow run history and deselect the active workflow so the
        // user starts fresh in Ask mode.
        setActiveWorkflowId(null);
        setWorkflowRunHistory([]);
        setActiveMode('ask');
    }, [swarm]);

    const { showLeftPanel, setShowLeftPanel } = usePanels();
    const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
    const [leftPanelTab, setLeftPanelTab] = useState("conversations");
    const { resetChat, isResetting } = useResetChat(API_URL);
    const { deleteChat, isDeleting } = useDeleteChat(API_URL);
    const { renameChat, isRenaming } = useRenameChat(API_URL);

    useEffect(() => {
        sessionStorage.setItem("conversationId",conversationId);
    }, [conversationId]);

    const handleResetChat = useCallback(async () => {
        // Optimistically reset the UI immediately by switching to a fresh conversation.
        const newId = crypto.randomUUID();
        setConversationId(newId);
        setActiveConversationId(null);
        setActiveSwarmSessionId(null);
        sessionStorage.setItem("conversationId",newId);
        swarm.resetSwarm();
        // Clear each mode's active view so the empty-state headline appears.
        // activeMode is intentionally NOT changed — the user stays in whatever
        // mode they were in and gets a fresh blank state to start a new session.
        setWorkflowRunHistory([]);
        setActiveWorkflowId(null);

        // Perform the server-side reset in the background.
        const success = await resetChat();
        if (success) {
            refreshHistories();
        }
    }, [resetChat, refreshHistories, swarm, setActiveWorkflowId]);

    const handleSelectConversation = useCallback(async (id, dbStatus = null) => {
        // Switch the visible conversation without disturbing any in-flight requests
        // for the previous conversation or for `id` itself (if it's already loading
        // in the background).
        setActiveMode('ask');
        setActiveWorkflowId(null);
        setWorkflowRunHistory([]);
        setConversationId(id);
        setActiveConversationId(id);
        setActiveSwarmSessionId(null);
        sessionStorage.setItem("conversationId",id);
        setQuestion("");
        swarm.resetSwarm();

        // Skip the server fetch if the session is actively streaming (don't clobber
        // live data) or if it was just completed cross-session (already reloaded by
        // onHistoriesFetched). Re-fetch if it has stale content from when it was running.
        const existingSession = chatSessions.getSession(id);
        if (existingSession.loading) return;
        const wasRunning = dbStatus === 'running' || runningConversationIds.has(id);
        const hasContent = existingSession.chatHistory.length > 0;
        if (hasContent && !wasRunning) return;

        try {
            const historyFromServer = await fetchConversationHistory(userId, id);
            const mapped = (historyFromServer || [])
                .map(historyEntryFromServer)
                .sort((a, b) => a.index - b.index);

            // wasRunning: in-memory data was loaded while the server was still
            // processing, so it may be missing the final expansive/evaluation
            // answer. Trust the server to have the completed data.
            chatSessions.loadSessionHistory(id, mapped, wasRunning);

            // Clear a stale running entry only when the DB itself says the
            // conversation is NOT running (dbStatus !== 'running').  If the DB
            // still says 'running', the server is actively processing — clearing
            // runningConversationIds here would kill the 3 s cross-session
            // polling before Phase 2 finishes, leaving the chat empty until the
            // 8 s sidebar poll catches up.
            if (dbStatus !== 'running' && runningConversationIds.has(id) &&
                    mapped.some(e => e.preliminary_answer)) {
                setRunningConversationIds(prev => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
                refreshHistories();
            }
        } catch (e) {
            console.error("Failed to load conversation history:", e);
            chatSessions.loadSessionHistory(id, []);
        }
    }, [userId, fetchConversationHistory, chatSessions, swarm, runningConversationIds, refreshHistories]);

    const handleDeleteConversation = useCallback(async (idToDelete) => {
        if (!userId) return;
        const success = await deleteChat(idToDelete, userId);
        if (success) {
            console.log("Conversation deleted successfully:", idToDelete);
            refreshHistories();
            if (conversationId === idToDelete) {
                // If the active conversation was deleted, reset the chat view
                await handleResetChat();
            }
        } else {
            console.error("Failed to delete conversation:", idToDelete);
        }
    }, [userId, deleteChat, refreshHistories, conversationId, handleResetChat]);

    const handleRenameConversation = useCallback(async (idToRename, newName) => {
        if (!userId) return;
        const success = await renameChat(idToRename, newName);
        if (success) {
            console.log("Conversation renamed successfully:", idToRename);
            refreshHistories();
        } else {
            console.error("Failed to rename conversation:", idToRename);
        }
    }, [userId, renameChat, refreshHistories]);

    // Refresh swarm history list whenever a session becomes terminal, and drop
    // its pending entry now that the real record will appear via the fetch.
    useEffect(() => {
        if (swarm.isTerminal && swarm.sessionId) {
            refreshSwarmHistories();
            setPendingSwarmSessions(prev => prev.filter(s => s.session_id !== swarm.sessionId));
        }
    }, [swarm.isTerminal, swarm.sessionId, refreshSwarmHistories]);

    const handleDeleteSwarmSession = useCallback(async (sessionId) => {
        const success = await deleteSwarmSession(sessionId);
        if (success) {
            refreshSwarmHistories();
            if (activeSwarmSessionId === sessionId) {
                swarm.resetSwarm();
                setActiveSwarmSessionId(null);
            }
        }
    }, [deleteSwarmSession, refreshSwarmHistories, activeSwarmSessionId, swarm]);

    const handleRenameSwarmSession = useCallback(async (sessionId, name) => {
        const success = await renameSwarmSession(sessionId, name);
        if (success) refreshSwarmHistories();
    }, [renameSwarmSession, refreshSwarmHistories]);

    const handleSelectSwarmSession = useCallback((session) => {
        const sid = session.session_id;
        const cid = session.conversation_id;

        // Switch to swarm mode and set conversation context
        setActiveMode('swarm');
        setConversationId(cid);
        setActiveConversationId(null);
        setActiveSwarmSessionId(sid);
        sessionStorage.setItem("conversationId",cid);

        setQuestion("");

        // Load the swarm session (replays events + summary from DB)
        swarm.loadSession(sid);
    }, [swarm, setQuestion]);

    const appStyles = getAppStyles();

    return (
        <div className="app-container" style={appStyles.appContainer}>
            <Toaster position="top-center" />
            <Helmet>
                <title>Stackform - Custom AI Stack</title>
                <meta name="description" content="AI-powered research assistant" />
            </Helmet>
            {showTutorial && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 3000,
                    }}
                    onClick={() => setShowTutorial(false)}
                >
                    <div
                        style={{
                            backgroundColor: "var(--panel-background, #fff)",
                            color: "var(--text-color, #000)",
                            padding: "20px",
                            borderRadius: "8px",
                            maxWidth: "600px",
                            maxHeight: "80vh",
                            overflowY: "auto",
                            position: "relative",
                            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                            border: "1px solid var(--panel-border, #ccc)",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setShowTutorial(false)}
                            style={{
                                position: "absolute",
                                top: "10px",
                                right: "10px",
                                background: "none",
                                border: "none",
                                fontSize: "1.5rem",
                                cursor: "pointer",
                                color: "inherit",
                            }}
                        >
                            &times;
                        </button>
                        <TutorialContent
                            onDismiss={dismissTutorial}
                            onRestore={restoreTutorial}
                            showAtStartup={user?.show_tutorial ?? false}
                        />
                    </div>
                </div>
            )}

            <div className="app-content-area" style={appStyles.contentArea}>
                <div
                    className={`left-panel-desktop themed-panel-content${leftPanelCollapsed ? ' left-panel-collapsed' : ''}`}
                    style={{
                        ...appStyles.sidePanel,
                        position: "relative",
                        overflow: "visible",
                        // PanelShell manages all internal padding; outer container has none.
                        padding: 0,
                    }}
                >
                    <LeftPanel
                        collapsed={leftPanelCollapsed}
                        onToggleCollapse={() => setLeftPanelCollapsed(c => !c)}
                        API_URL={API_URL}
                        user={user}
                        scrollAreaStyle={appStyles.scrollArea}
                        activeConversationId={activeConversationId}
                        onSelectConversation={handleSelectConversation}
                        onDeleteConversation={handleDeleteConversation}
                        onRenameConversation={handleRenameConversation}
                        isDeleting={isDeleting}
                        isRenaming={isRenaming}
                        onResetChat={handleResetChat}
                        isResetting={isResetting}
                        historyRefreshTrigger={historyRefreshTrigger}
                        loadingConversationIds={loadingConversationIds}
                        pendingConversations={pendingConversations}
                        onHistoriesFetched={onHistoriesFetched}
                        justCompletedIds={justCompletedIds}
                        swarmSessions={swarmSessions}
                        pendingSwarmSessions={pendingSwarmSessions}
                        loadingSwarmSessionIds={loadingSwarmSessionIds}
                        onSelectSwarmSession={handleSelectSwarmSession}
                        onDeleteSwarmSession={handleDeleteSwarmSession}
                        onRenameSwarmSession={handleRenameSwarmSession}
                        isDeletingSwarm={isDeletingSwarm}
                        isRenamingSwarm={isRenamingSwarm}
                        activeSwarmSessionId={activeSwarmSessionId}
                        swarmSessionsLoading={swarmSessionsLoading}
                        onModeChange={setActiveMode}
                        activeMode={activeMode}
                        workflowRuns={userWorkflowRuns}
                        runningWorkflowRunIds={runningWorkflowRunIds}
                        workflowList={workflowList}
                        onRenameWorkflowRun={handleRenameWorkflowRun}
                        onDeleteWorkflowRun={handleDeleteWorkflowRun}
                        onSelectWorkflowRun={handleSelectWorkflowRun}
                        activeWorkflowRunId={workflowRunHistory[workflowRunHistory.length - 1]?.runId ?? null}
                        activeTab={leftPanelTab}
                        onTabChange={setLeftPanelTab}
                        modelSettings={modelSettings}
                        theme={theme}
                        toggleTheme={toggleTheme}
                        selectedApis={selectedApis}
                        setSelectedApis={setSelectedApis}
                        refreshHistories={refreshHistories}
                        resetMainPanel={resetMainPanel}
                        refreshModelSettings={refreshModelSettings}
                    />
                </div>

                <MainPanelContainer
                    question={question}
                    setQuestion={setQuestion}
                    pendingQuestion={pendingQuestion}
                    pendingFiles={pendingFiles}
                    askQuestion={askQuestion}
                    cancelRequest={cancelRequest}
                    stopAsk={stopAsk}
                    isActiveConvServerRunning={isActiveConvServerRunning}
                    loading={loading}
                    response={response}
                    elapsed={elapsed}
                    mainPanelStyle={appStyles.mainPanel}
                    selectedApis={selectedApis}
                    availableAgents={availableAgents}
                    chatHistory={chatHistory}
                    activeMode={activeMode}
                    setActiveMode={setActiveMode}
                    swarm={swarm}
                    progressSteps={progressSteps}
                    files={files}
                    setFiles={setFiles}
                    streamingPhase={streamingPhase}
                    answerMode={answerMode}
                    setAnswerMode={setAnswerMode}
                    generateExpansiveAnswer={generateExpansiveAnswer}
                    generatingExpansiveIndex={generatingExpansiveIndex}
                    generateEvaluationReport={generateEvaluationReport}
                    generatingEvaluationIndex={generatingEvaluationIndex}
                    conversationId={conversationId}
                    API_URL={API_URL}
                    onSwarmStart={onSwarmStart}
                    workflowList={workflowList}
                    activeWorkflowId={activeWorkflowId}
                    onWorkflowSelect={handleWorkflowSelect}
                    workflowRunHistory={workflowRunHistory}
                    workflowActiveRuns={workflowActiveRuns}
                    onRunWorkflow={handleRunWorkflow}
                    cancelWorkflowRun={cancelWorkflowRun}
                />

            </div>

            <PanelsContainer
                modelSettings={modelSettings}
                showLeftPanel={showLeftPanel}
                setShowLeftPanel={setShowLeftPanel}
                leftPanelTab={leftPanelTab}
                setLeftPanelTab={setLeftPanelTab}
                onResetChat={handleResetChat}
                isResetting={isResetting}
                API_URL={API_URL}
                user={user}
                activeConversationId={activeConversationId}
                onSelectConversation={handleSelectConversation}
                onDeleteConversation={handleDeleteConversation}
                onRenameConversation={handleRenameConversation}
                isDeleting={isDeleting}
                isRenaming={isRenaming}
                historyRefreshTrigger={historyRefreshTrigger}
                loadingConversationIds={loadingConversationIds}
                pendingConversations={pendingConversations}
                onHistoriesFetched={onHistoriesFetched}
                justCompletedIds={justCompletedIds}
                swarmSessions={swarmSessions}
                pendingSwarmSessions={pendingSwarmSessions}
                loadingSwarmSessionIds={loadingSwarmSessionIds}
                onSelectSwarmSession={handleSelectSwarmSession}
                onDeleteSwarmSession={handleDeleteSwarmSession}
                onRenameSwarmSession={handleRenameSwarmSession}
                isDeletingSwarm={isDeletingSwarm}
                isRenamingSwarm={isRenamingSwarm}
                activeSwarmSessionId={activeSwarmSessionId}
                swarmSessionsLoading={swarmSessionsLoading}
                onModeChange={setActiveMode}
                activeMode={activeMode}
                workflowRuns={userWorkflowRuns}
                runningWorkflowRunIds={runningWorkflowRunIds}
                workflowList={workflowList}
                onRenameWorkflowRun={handleRenameWorkflowRun}
                onDeleteWorkflowRun={handleDeleteWorkflowRun}
                onSelectWorkflowRun={handleSelectWorkflowRun}
                activeWorkflowRunId={workflowRunHistory[workflowRunHistory.length - 1]?.runId ?? null}
                theme={theme}
                toggleTheme={toggleTheme}
                selectedApis={selectedApis}
                setSelectedApis={setSelectedApis}
                refreshHistories={refreshHistories}
                resetMainPanel={resetMainPanel}
                refreshModelSettings={refreshModelSettings}
            />

        </div>
    );
}

export default App;
